/**
 * <StudentQuestsPage> — « Mes quêtes » de l'élève (page).
 *
 * L'écran d'aujourd'hui, rhabillé : une carte par quête, donc une par champion, à l'étape où
 * l'élève en est (celui qui la porte, l'étape, sa phrase, son avancement, ce qu'elle rapporte).
 * Toute la carte ouvre le détail de la quête. Une quête n'est jamais « pas encore débloquée »
 * dans cette liste : ce sont ses étapes à venir qui le sont, dans son détail.
 *
 * On y arrive depuis « Mon compte » (la ligne « Quêtes »). La croix referme, la flèche relit.
 *
 * Au large, les quatre cartes se posent deux par deux : la liste garde son ordre de lecture,
 * ligne par ligne.
 */
import React from "react"
import { View } from "react-native"

import { ClosableHeader } from "../../molecules/ClosableHeader"
import { EmptyState } from "../../molecules/EmptyState"
import { QuestCard, QuestChampion, type UIQuest } from "../../organisms/QuestCard"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { questsGridStyles } from "./questsGrid"
import { StudentQuestsSkeleton } from "./StudentQuestsSkeleton"

export type StudentQuestsPageProps = {
    /** Les quêtes de l'élève, dans l'ordre du catalogue. `null` tant qu'elles ne sont pas là. */
    quests: UIQuest[] | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** La croix de l'en-tête. */
    onClose: () => void
    /** Une relecture est en cours : la flèche de l'en-tête tourne. */
    refreshing?: boolean
    onRefresh?: () => void
    /** Ouvre le détail d'une quête. Absent : les cartes ne pressent pas. */
    onOpenQuest?: (quest: UIQuest) => void
    testID?: string
}

const TEXTS = {
    title: "Mes quêtes",
    empty: "Aucune quête pour le moment",
}

export function StudentQuestsPage(props: StudentQuestsPageProps) {
    const {
        quests,
        loading = false,
        error,
        onRetry,
        onClose,
        refreshing = false,
        onRefresh,
        onOpenQuest,
        testID,
    } = props

    const header = (
        <ClosableHeader title={TEXTS.title} onClose={onClose} refreshing={refreshing} onRefresh={onRefresh} />
    )

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<StudentQuestsSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {quests != null && quests.length === 0 && <EmptyState iconName="flag" title={TEXTS.empty} />}
            {quests != null && quests.length > 0 && (
                <View style={questsGridStyles.grid}>
                    {quests.map((quest) => (
                        <View key={quest.id} style={questsGridStyles.cell}>
                            <QuestCard
                                quest={quest}
                                champion={<QuestChampion name={quest.champion} />}
                                onPress={onOpenQuest != null ? () => onOpenQuest(quest) : undefined}
                            />
                        </View>
                    ))}
                </View>
            )}
        </PageTemplate>
    )
}
